// Profile's badge shelf: all fifteen, earned ones in their metal, locked ones
// dark with a ring that fills toward tier I, hidden ones a question mark until
// they happen. A tap opens the badge: its ladder, the next step, and SHARE.
import { useEffect, useState } from 'react';
import { createPortal } from 'react-dom';
import { getPlayerId } from '../lib/playerId';
import { logAppEvent } from '../lib/appEvents';
import {
  BADGES, badgeArt, badgeSpec, earnedLine, loadBadges, nextStep, rarityLine, tierLabel,
  TIER_LABEL, TIER_METAL, TIER_NUMERAL, TIER_TEXT, type BadgeState, type BadgeSync, type Tier,
} from '../lib/badges';
import { shareBadge } from '../lib/badgeShare';
import { SharePreview } from './SharePreview';

// Once per app run: the shelf re-renders on every Profile visit, and the
// question is whether players look at it at all, not how often React mounts.
let shelfLogged = false;

export function BadgeShelf({ nickname }: { nickname: string }) {
  const [sync, setSync] = useState<BadgeSync | null>(null);
  const [failed, setFailed] = useState(false);
  const [open, setOpen] = useState<BadgeState | null>(null);

  useEffect(() => {
    let active = true;
    (async () => {
      const s = await loadBadges(await getPlayerId());
      if (!active) return;
      if (!s) { setFailed(true); return; }
      setSync(s);
      if (!shelfLogged) {
        shelfLogged = true;
        logAppEvent('badge_shelf_open', { earned: s.badges.filter((b) => b.tier > 0).length });
      }
    })();
    return () => { active = false; };
  }, []);

  // Before the server answers (or without 20261012) the shelf takes no room:
  // an empty grid would read as fifteen badges lost.
  if (failed || !sync) return null;
  const earned = sync.badges.filter((b) => b.tier > 0).length;

  return (
    <div className="compact-history-container badge-shelf">
      <div className="badge-shelf-head">
        <h3 className="section-label" style={{ margin: 0 }}>BADGES</h3>
        <span className="badge-shelf-count">{earned} OF {BADGES.length}</span>
      </div>
      <div className="badge-grid">
        {sync.badges.map((b) => <Slot key={b.id} state={b} onOpen={() => setOpen(b)} />)}
      </div>
      {open && <BadgeSheet state={open} nickname={nickname} onClose={() => setOpen(null)} />}
    </div>
  );
}

function Slot({ state, onOpen }: { state: BadgeState; onOpen: () => void }) {
  const spec = badgeSpec(state.id);
  const locked = state.tier === 0;
  if (locked && spec.hidden) {
    return (
      <button className="badge-slot" onClick={onOpen} aria-label="Hidden badge">
        <span className="badge-slot-art badge-hidden" aria-hidden>?</span>
        <span className="badge-slot-name">HIDDEN</span>
      </button>
    );
  }
  const step = nextStep(state);
  const frac = locked && step ? Math.min(1, step.have / step.need) : 0;
  return (
    <button className={`badge-slot ${locked ? 'locked' : ''}`} onClick={onOpen}
      aria-label={`${spec.name}${locked ? ', locked' : `, ${tierLabel(spec, state.tier as Tier) ?? 'earned'}`}`}>
      <span className="badge-slot-art">
        <img src={badgeArt(state.id, state.tier)} alt="" width={60} height={60} loading="lazy" />
        {locked && (
          <svg className="badge-ring" viewBox="0 0 70 70" aria-hidden>
            <circle cx="35" cy="35" r="33" fill="none" stroke="rgba(255,255,255,0.10)" strokeWidth="3" />
            {frac > 0 && (
              <circle cx="35" cy="35" r="33" fill="none" stroke="#7EA8FF" strokeWidth="3" strokeLinecap="round"
                pathLength="100" strokeDasharray={`${Math.round(frac * 100)} 100`} transform="rotate(-90 35 35)" />
            )}
          </svg>
        )}
      </span>
      <span className="badge-slot-name">{spec.name}</span>
      {locked
        ? step && <span className="badge-slot-sub">{step.have} / {step.need}</span>
        : <span className="badge-slot-sub" style={{ color: TIER_TEXT[state.tier as Tier] }}>
            {spec.tiers === 1 ? 'EARNED' : TIER_LABEL[state.tier as Tier]}
          </span>}
    </button>
  );
}

function BadgeSheet({ state, nickname, onClose }: { state: BadgeState; nickname: string; onClose: () => void }) {
  const spec = badgeSpec(state.id);
  const earned = state.tier > 0;
  const secret = spec.hidden && !earned;
  const step = nextStep(state);
  const rarity = earned ? rarityLine(state.held) : null;
  const [busy, setBusy] = useState(false);
  const [copied, setCopied] = useState(false);
  const [preview, setPreview] = useState<{ url: string; text: string } | null>(null);

  useEffect(() => {
    const onKey = (e: KeyboardEvent) => { if (e.key === 'Escape') onClose(); };
    window.addEventListener('keydown', onKey);
    return () => window.removeEventListener('keydown', onKey);
  }, [onClose]);

  async function onShare() {
    if (busy) return;
    setBusy(true);
    const done = await shareBadge(state, nickname);
    setBusy(false);
    if (done.outcome === 'copied') { setCopied(true); setTimeout(() => setCopied(false), 2000); }
    if (done.previewUrl) setPreview({ url: done.previewUrl, text: done.text });
  }

  // The card preview is the sheet's sibling, not its child: a tap that closes
  // the preview must not bubble on and close the sheet under it.
  return createPortal(<>
    <div className="badge-sheet" role="dialog" aria-label={secret ? 'Hidden badge' : spec.name} onClick={onClose}>
      <div className="badge-sheet-box" onClick={(e) => e.stopPropagation()}>
        {secret ? (
          <>
            <span className="badge-sheet-art badge-hidden" aria-hidden>?</span>
            <span className="badge-name">HIDDEN BADGE</span>
            <span className="badge-line">It shows itself when it happens. Keep playing.</span>
          </>
        ) : (
          <>
            <img className={`badge-sheet-art ${earned ? '' : 'locked'}`} src={badgeArt(state.id, state.tier)} alt="" width={140} height={140} />
            <span className="badge-name" style={{ fontSize: '1.3rem' }}>{spec.name}</span>
            {earned && tierLabel(spec, state.tier as Tier) && (
              <span className="badge-tier" style={{ color: TIER_TEXT[state.tier as Tier] }}>{tierLabel(spec, state.tier as Tier)}</span>
            )}
            <span className="badge-line">{earned ? earnedLine(state.id, state.tier as Tier) : 'Not earned yet.'}</span>
            {rarity && <span className="badge-rarity">{rarity}</span>}
            {spec.tiers > 1 && (
              <ol className="badge-ladder">
                {spec.goals.map((g, i) => {
                  const t = (i + 1) as Tier;
                  const done = state.tier >= t;
                  return (
                    <li key={t} className={done ? 'done' : ''}>
                      <span className="badge-ladder-tier" style={{ color: done ? TIER_TEXT[t] : undefined, borderColor: done ? TIER_METAL[t] : undefined }}>
                        {TIER_NUMERAL[t]}
                      </span>
                      <span>{g}</span>
                      {done && <span className="badge-ladder-tick" aria-label="earned">&#10003;</span>}
                    </li>
                  );
                })}
              </ol>
            )}
            {step && (
              <div className="badge-progress" style={{ width: '100%' }}>
                <div className="badge-progress-row"><span>{spec.tiers === 1 ? 'Progress' : 'Next'}</span><span>{step.have} / {step.need}</span></div>
                <div className="badge-progress-bar">
                  <i style={{ width: `${Math.max(4, (100 * step.have) / step.need)}%`, background: TIER_METAL[Math.min(3, state.tier + 1) as Tier] }} />
                </div>
              </div>
            )}
          </>
        )}
        <div className="badge-sheet-actions">
          {earned && <button className="btn btn-primary" onClick={onShare} disabled={busy}>{copied ? 'COPIED' : 'SHARE'}</button>}
          <button className="btn btn-ghost" onClick={onClose}>CLOSE</button>
        </div>
      </div>
    </div>
    {preview && <SharePreview url={preview.url} text={preview.text} fileName="wot-badge.png" onClose={() => setPreview(null)} />}
  </>, document.body);
}
